import { useCallback, useEffect, useState } from "react";
import { Download, Eye, EyeOff, Star, Trash2, CheckSquare, Square, Heart } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl, slDateTime, fmtBytes } from "@/lib/api";
import { MediaThumb } from "@/components/MediaCard";
import Lightbox from "@/components/Lightbox";
import { useZip, ZipDialog } from "@/components/manage/Zip";

const IconBtn = ({ onClick, title, active, children, testid }) => (
  <button onClick={onClick} title={title} aria-label={title} className={`w-8 h-8 rounded-full grid place-items-center transition-colors ${active ? "bg-[#231F20] text-white" : "bg-white/90 hover:bg-[#EADBCE] text-[#231F20]"}`} data-testid={testid}>{children}</button>
);

export default function MediaManager({ wedding, onChange }) {
  const wid = wedding.id;
  const [filter, setFilter] = useState({ type: "", visibility: "", favorites: false, uploader: "" });
  const [data, setData] = useState({ items: [], uploaders: [] });
  const [sel, setSel] = useState(new Set());
  const [open, setOpen] = useState(null);
  const zip = useZip(wid);

  const load = useCallback(() => api.get(`/manage/weddings/${wid}/media`, { params: { type: filter.type || undefined, visibility: filter.visibility || undefined, favorites: filter.favorites || undefined, uploader: filter.uploader || undefined } }).then((r) => setData(r.data)), [wid, filter]);
  useEffect(() => { load(); const t = setInterval(() => document.visibilityState === "visible" && load(), 20000); return () => clearInterval(t); }, [load]);

  const patch = async (m, body) => {
    try {
      const r = await api.patch(`/manage/weddings/${wid}/media/${m.id}`, body);
      setData((d) => ({ ...d, items: d.items.map((x) => (x.id === m.id ? r.data : x)) }));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (m) => {
    if (!window.confirm("Ali res želite trajno izbrisati to datoteko?")) return;
    await api.delete(`/manage/weddings/${wid}/media/${m.id}`);
    setData((d) => ({ ...d, items: d.items.filter((x) => x.id !== m.id) }));
    setOpen(null);
    toast.success("Datoteka je izbrisana.");
    onChange();
  };
  const bulk = async (action) => {
    if (action === "delete" && !window.confirm(`Izbrisati ${sel.size} datotek?`)) return;
    await api.post(`/manage/weddings/${wid}/media/bulk`, { ids: [...sel], action });
    setSel(new Set());
    load();
    if (action === "delete") onChange();
  };
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const chip = (active) => `px-4 py-2 rounded-full text-sm border whitespace-nowrap ${active ? "bg-[#231F20] text-white border-[#231F20]" : "bg-[#FFFDF9] border-[#DFD5C5]"}`;

  return (
    <div className="space-y-5" data-testid="media-manager">
      <div className="card-lux p-4 flex flex-wrap gap-2 items-center">
        <span className="text-sm text-[#6C6560] mr-2">Prenos:</span>
        <button onClick={() => zip.start("all")} className="btn-dark px-4 py-2 text-sm flex items-center gap-2" data-testid="couple-download-zip-button"><Download size={15} /> Vse (ZIP)</button>
        <button onClick={() => zip.start("photos")} className="btn-line px-4 py-2 text-sm" data-testid="zip-photos-button">Vse fotografije</button>
        <button onClick={() => zip.start("videos")} className="btn-line px-4 py-2 text-sm" data-testid="zip-videos-button">Vsi videi</button>
        <button onClick={() => zip.start("favorites")} className="btn-line px-4 py-2 text-sm" data-testid="zip-favorites-button">Najljubše</button>
        {sel.size > 0 && <button onClick={() => zip.start("selected", [...sel])} className="btn-gold px-4 py-2 text-sm" data-testid="zip-selected-button">Izbrane ({sel.size})</button>}
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
        {[["", "Vse"], ["photo", "Fotografije"], ["video", "Videi"]].map(([v, l]) => <button key={v} className={chip(filter.type === v)} onClick={() => setFilter({ ...filter, type: v })} data-testid={`manage-filter-${v || "all"}`}>{l}</button>)}
        <button className={chip(filter.visibility === "hidden")} onClick={() => setFilter({ ...filter, visibility: filter.visibility === "hidden" ? "" : "hidden" })} data-testid="manage-filter-hidden">Skrite</button>
        <button className={chip(filter.favorites)} onClick={() => setFilter({ ...filter, favorites: !filter.favorites })} data-testid="manage-filter-favorites">Najljubše</button>
        <select value={filter.uploader} onChange={(e) => setFilter({ ...filter, uploader: e.target.value })} className="px-3 py-2 rounded-full text-sm border border-[#DFD5C5] bg-[#FFFDF9]" data-testid="manage-filter-uploader">
          <option value="">Vsi gostje</option>{data.uploaders.map((u) => <option key={u}>{u}</option>)}
        </select>
      </div>

      {sel.size > 0 && (
        <div className="sticky top-20 z-30 card-lux p-3 flex flex-wrap gap-2 items-center" data-testid="bulk-bar">
          <span className="text-sm mr-2">Izbranih: {sel.size}</span>
          <button onClick={() => bulk("favorite")} className="btn-line px-3 py-1.5 text-xs">Med najljubše</button>
          <button onClick={() => bulk("hide")} className="btn-line px-3 py-1.5 text-xs" data-testid="bulk-hide">Skrij</button>
          <button onClick={() => bulk("show")} className="btn-line px-3 py-1.5 text-xs">Prikaži</button>
          <button onClick={() => bulk("delete")} className="px-3 py-1.5 text-xs rounded-full bg-red-700 text-white" data-testid="bulk-delete">Izbriši</button>
          <button onClick={() => setSel(new Set())} className="text-xs underline ml-auto">Počisti izbor</button>
        </div>
      )}

      <p className="text-sm text-[#6C6560]" data-testid="manage-media-count">{data.items.length} datotek</p>
      {data.items.length === 0 ? <p className="text-center py-16 text-[#6C6560]" data-testid="manage-media-empty">Ni datotek.</p> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {data.items.map((m, i) => (
            <div key={m.id} className={`relative group rounded-xl ${m.visibility === "hidden" ? "opacity-50" : ""}`} data-testid="manage-media-item">
              <div className="aspect-square overflow-hidden rounded-xl [&>button]:h-full [&>button]:!aspect-auto"><MediaThumb item={m} onOpen={() => setOpen(i)} /></div>
              <button onClick={() => toggle(m.id)} className="absolute top-2 left-2 text-white drop-shadow" data-testid="manage-media-select" aria-label="Izberi">{sel.has(m.id) ? <CheckSquare size={22} /> : <Square size={22} />}</button>
              <div className="absolute bottom-2 right-2 flex gap-1">
                <IconBtn onClick={() => patch(m, { is_favorite: !m.is_favorite })} active={m.is_favorite} title="Najljubša" testid="manage-media-favorite"><Star size={14} /></IconBtn>
                <IconBtn onClick={() => patch(m, { visibility: m.visibility === "hidden" ? "visible" : "hidden" })} title={m.visibility === "hidden" ? "Prikaži" : "Skrij"} testid="manage-media-visibility">{m.visibility === "hidden" ? <EyeOff size={14} /> : <Eye size={14} />}</IconBtn>
                <IconBtn onClick={() => remove(m)} title="Izbriši" testid="manage-media-delete"><Trash2 size={14} /></IconBtn>
              </div>
              <p className="text-[11px] text-[#6C6560] truncate mt-1 px-0.5">{m.uploader_name} · <Heart size={10} className="inline" /> {m.hearts}</p>
            </div>
          ))}
        </div>
      )}
      <Lightbox items={data.items} index={open} onClose={() => setOpen(null)} onIndex={setOpen} actions={(m) => (
        <>
          <span className="text-xs text-white/50 hidden sm:inline">{m.original_filename} · {fmtBytes(m.size)}</span>
          <a href={fileUrl(m.download_url)} className="px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 flex items-center gap-2 text-sm" data-testid="manage-lightbox-download"><Download size={16} /> Original</a>
          <button onClick={() => remove(m)} className="px-4 py-2 rounded-full bg-white/10 hover:bg-red-700 text-sm" data-testid="manage-lightbox-delete"><Trash2 size={16} /></button>
        </>
      )} />
      <ZipDialog job={zip.job} onClose={zip.close} />
    </div>
  );
}
